import { useDrag, useWheel } from '@use-gesture/react';
import * as React from 'react';

import { dragScrollLeft, resolveWheelDelta } from './utils';

/**
 * Горизонтальный скролл контейнера мышью: drag (зажать и тащить) и колесо.
 *
 * Тач отдаётся нативному скроллу: handler реагирует только на pointerType 'mouse',
 * а контейнеру следует задать `touch-action: pan-x`. scroll-snap, заданный в CSS-классе
 * контейнера, временно отключается на время drag (инлайном) и возвращается после
 * (пустая строка → снова применяется правило из класса).
 *
 * Dead-zone (tapsThreshold) и гашение клика после drag обеспечивает сам @use-gesture
 * через `filterTaps`: клик, последовавший за drag, гасится (preventDefault + stopPropagation),
 * поэтому drag по лейблам не открывает карточку-ссылку и тултип.
 */
export function useDragScroll(ref: React.RefObject<HTMLElement>, isEnabled = true): void {
  const startLeft = React.useRef(0);

  useDrag(
    ({ first, last, movement: [movementX], event }) => {
      const el = ref.current;
      // Только мышь: тач/перо отдаём нативному скроллу. `'pointerType' in event` заодно сужает
      // union-тип события use-gesture (PointerEvent | Touch/Mouse/KeyboardEvent) до PointerEvent.
      if (!el || !('pointerType' in event) || event.pointerType !== 'mouse') {
        return;
      }

      if (first) {
        startLeft.current = el.scrollLeft;
        el.style.scrollSnapType = 'none';
      }

      el.scrollLeft = dragScrollLeft(startLeft.current, movementX);

      if (last) {
        el.style.scrollSnapType = '';
      }
    },
    {
      target: ref,
      enabled: isEnabled,
      axis: 'x',
      filterTaps: true,
      // capture: false — без setPointerCapture (не перехватывает тач, не падает в jsdom),
      // move/up делегируются на window. keys: false — отключаем клавиатурный drag.
      pointer: { capture: false, keys: false },
      eventOptions: { passive: false },
    },
  );

  useWheel(
    ({ event, delta: [deltaX, deltaY] }) => {
      const el = ref.current;
      if (!el) {
        return;
      }

      const delta = resolveWheelDelta(el, deltaX, deltaY);
      if (delta === null) {
        return;
      }

      el.scrollLeft += delta;
      // passive: false (в конфиге ниже) позволяет preventDefault: вертикальное колесо
      // превращается в горизонтальный скролл, а не скроллит страницу.
      event.preventDefault();
    },
    {
      target: ref,
      enabled: isEnabled,
      eventOptions: { passive: false },
    },
  );
}
